/**
 * App shell: resolve the server's auth mode, gate on the matching sign-in —
 * the dev display-name prompt (AUTH_MODE=dev/iap) or Firebase email+password
 * (AUTH_MODE=firebase, same accounts as the rainvic-tool desktop app) — then
 * routes.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { fetchAuthMode, fetchMe, type AuthModeValue } from "./core/api.js";
import { colorForName, getStoredName, setActiveIdentity, storeName } from "./core/identity.js";
import { RouteReporter } from "./core/RouteReporter.js";
import { AdminScreen } from "./features/admin/AdminScreen.js";
import { BoardScreen } from "./features/board/BoardScreen.js";
import { Dashboard } from "./features/dashboard/Dashboard.js";
import { StoryboardLab } from "./features/storyboard-lab/StoryboardLab.js";
import { DebugLogPanel } from "./features/debug/DebugLogPanel.js";
import { loadAiModels } from "./features/ai-models/load.js";
import { loadPresenterPhotos } from "./features/presenter-photos/load.js";
import { loadTextKinds } from "./features/text-kinds/load.js";
import { KolaRoot } from "./ui/KolaRoot.js";
import { VersionBadge } from "./ui/VersionBadge.js";

export function App() {
  const [mode, setMode] = useState<AuthModeValue | null>(null);

  useEffect(() => {
    void fetchAuthMode().then(setMode);
  }, []);

  // The two admin-defined registries — the `/` kinds (F-17-type) and the AI
  // model list every picker renders. Loaded once the gate has let the user
  // through (both routes need an identity) and again whenever a board opens
  // (BoardScreen), so a kind or a model added on /admin reaches every open tab
  // by its next board open at the latest.
  useEffect(() => {
    if (mode === null) return;
    void loadTextKinds();
    void loadAiModels();
    // The presenter photo library (F-134) rides the same schedule: the
    // thumbnail panel needs it to say where the presenter's face comes from.
    void loadPresenterPhotos();
  }, [mode]);

  // One unauthenticated round-trip; nothing meaningful to paint before it.
  if (mode === null) return null;

  return (
    <KolaRoot>
      {mode === "firebase" ? (
        <FirebaseGate />
      ) : (
        <DevGate>
          <AppRoutes />
        </DevGate>
      )}
      <VersionBadge />
      <DebugLogPanel />
    </KolaRoot>
  );
}

/**
 * The screens, router-agnostic: the web shell wraps them in a BrowserRouter
 * (real URLs, the server's SPA fallback), the embedded shell in a
 * MemoryRouter driven by its host (embedded.tsx). Every screen below uses
 * only the router-neutral hooks, so neither shell needs to know the other.
 */
export function KolaRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/b/:boardId" element={<BoardScreen />} />
      <Route path="/admin" element={<AdminScreen />} />
      <Route path="/storyboard-lab" element={<StoryboardLab />} />
      <Route path="*" element={<Dashboard />} />
    </Routes>
  );
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <RouteReporter />
      <KolaRoutes />
    </BrowserRouter>
  );
}

/* ------------------------------------------------------------------ */
/* AUTH_MODE=dev / iap — first-load display-name prompt                */
/* ------------------------------------------------------------------ */

/**
 * AUTH_MODE=dev/iap: nothing renders until a display name is stored; the
 * embedded shell reuses it (embedded.tsx) with its own router as the child.
 */
export function DevGate({ children }: { children: ReactNode }) {
  const [name, setName] = useState<string | null>(() => getStoredName());

  if (name === null) {
    return (
      <NamePrompt
        onSubmit={(value) => {
          storeName(value);
          setName(value.trim());
        }}
      />
    );
  }

  return <>{children}</>;
}

function NamePrompt({ onSubmit }: { onSubmit: (name: string) => void }) {
  const [value, setValue] = useState("");
  const trimmed = value.trim();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (trimmed) onSubmit(trimmed);
  };

  return (
    <div className="name-prompt">
      <form className="name-prompt-card" onSubmit={submit}>
        <h1>Welcome to Kola</h1>
        <p>Pick a display name — teammates will see it on cursors, selections, and comments.</p>
        <input
          className="text-input"
          autoFocus
          placeholder="Your name"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          maxLength={80}
        />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!trimmed}
          style={trimmed ? { background: colorForName(trimmed), borderColor: colorForName(trimmed) } : undefined}
        >
          Start collaborating
        </button>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* AUTH_MODE=firebase — email/password sign-in, session mint, /me      */
/* ------------------------------------------------------------------ */

/**
 * Mint the kola_session cookie from the signed-in Firebase user and adopt the
 * SERVER-verified identity for presence — the same id/name every peer and
 * comment stamp will see, so it must come from /api/me, never from a client-
 * side guess.
 */
async function establishSession(): Promise<boolean> {
  try {
    const { ensureSession } = await import("./core/auth-session.js");
    if (!(await ensureSession())) return false;
    setActiveIdentity(await fetchMe());
    return true;
  } catch {
    return false;
  }
}

function FirebaseGate() {
  const [phase, setPhase] = useState<"initializing" | "login" | "ready">("initializing");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // 1. An already-live kola_session cookie means NO login screen at all.
      //    This is the rainvic-tool SSO path: the desktop app verifies its
      //    own Firebase user, mints the session server-side, and injects the
      //    cookie into this window before loading it — one login for both
      //    apps. (Also covers a plain browser whose cookie is still live.)
      try {
        setActiveIdentity(await fetchMe());
        if (!cancelled) setPhase("ready");
        return;
      } catch {
        // No live session — fall through to the Firebase flow.
      }
      if (cancelled) return;
      // 2. Restore a persisted Firebase session (sign in once per browser).
      const { waitForAuthState } = await import("./core/firebase.js");
      const user = await waitForAuthState();
      if (cancelled) return;
      const ready = user !== null && (await establishSession());
      if (!cancelled) setPhase(ready ? "ready" : "login");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (phase === "initializing") return null;
  if (phase === "login") return <FirebaseLogin onSignedIn={() => setPhase("ready")} />;
  return <AppRoutes />;
}

function FirebaseLogin({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ready = email.trim() !== "" && password !== "";

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const { signIn } = await import("./core/firebase.js");
        await signIn(email.trim(), password);
        if (await establishSession()) {
          onSignedIn();
          return;
        }
        setError("Signed in, but the board server rejected the session. Try again.");
      } catch {
        // Firebase's error codes are deliberately not surfaced verbatim —
        // "wrong password" vs "no such user" is an account-probing oracle.
        setError("Sign-in failed. Check the email and password.");
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div className="name-prompt">
      <form className="name-prompt-card" onSubmit={submit}>
        <h1>Sign in to Kola</h1>
        <p>Use your Rainvic Tool account — one login for both apps.</p>
        <input
          className="text-input"
          autoFocus
          type="email"
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <input
          className="text-input"
          type="password"
          autoComplete="current-password"
          placeholder="Password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        {error !== null && <p role="alert">{error}</p>}
        <button type="submit" className="btn btn-primary" disabled={!ready || busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
